"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import Image from "next/image"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ScrollArea } from "@/components/ui/scroll-area"
import { BASE_URL_IMAGE } from "@/utils/api"
import { FaChevronRight } from "react-icons/fa6"
import { IoMdSearch } from "react-icons/io"
import CourseCard from "@/components/coursecard";

interface Course {
  card_image: string
  name: string
  category: string
  instructor_name: string | null
  short_description: string
  course_mode: string
  course_type: string
  course_level: string
  slug: string
}
interface CourseData {
  [category: string]: {
    [subcategory: string]: Course[]
  }
}

export default function CourseListing({ data }: { data: CourseData }) {
  const [searchQuery, setSearchQuery] = useState("")
  const [subcategorySearch, setSubcategorySearch] = useState("")
  const [selectedCategory, setSelectedCategory] = useState<string>(Object.keys(data)[0])
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>("")
  const [showAllCourses, setShowAllCourses] = useState<boolean>(false)

  useEffect(() => {
    const subcategories = Object.keys(data[selectedCategory] || {})
    if (subcategories.length > 0) {
      setSelectedSubcategory(subcategories[0])
    }
  }, [selectedCategory])

  const filteredCourses = Object.values(data).flatMap(category => Object.values(category).flat()).filter(
    (course) =>
      course.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.short_description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.category?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const subcategories = data[selectedCategory] ? Object.keys(data[selectedCategory]) : []
  const getDisplayedCourses = () => {
    if (searchQuery) return filteredCourses
    if (showAllCourses) return Object.values(data[selectedCategory]).flat()
    if (selectedSubcategory) return data[selectedCategory][selectedSubcategory] || []
    return []
  }
  return (
    <div className="w-full  mx-auto py-6 px-4">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <h1 className="text-2xl font-bold">Our Courses</h1>
        <div className="relative w-full md:w-[300px]">
          <IoMdSearch className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search courses..."
            className="pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>
      <div className="relative w-full md:w-[300px] mb-4">
        <Input
          placeholder="Search subcategory..."
          className="pl-8"
          value={subcategorySearch}
          onChange={(e) => setSubcategorySearch(e.target.value)}
        />
      </div>
      {!searchQuery && (
        <Tabs defaultValue={selectedCategory} className="mb-8">
          <ScrollArea className="w-full whitespace-nowrap rounded-md border">
            <TabsList className="w-full justify-start">
              {Object.keys(data).map((category) => (
                <TabsTrigger
                  key={category}
                  value={category}
                  onClick={() => {
                    setSelectedCategory(category)
                    setSelectedSubcategory("")
                    setShowAllCourses(false)
                  }}
                  className="px-4"
                >
                  {category}
                </TabsTrigger>
              ))}
            </TabsList>
          </ScrollArea>
          <TabsContent key={selectedCategory} value={selectedCategory}>
            <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
              <Button
                variant={showAllCourses ? "default" : "outline"}
                onClick={() => setShowAllCourses(!showAllCourses)}
                className="whitespace-nowrap"
              >
                All Courses
              </Button>
              {subcategories.filter(sub => sub.toLowerCase().includes(subcategorySearch.toLowerCase())).map((subcategory) => (
                <Button
                  key={subcategory}
                  variant={selectedSubcategory === subcategory && !showAllCourses ? "default" : "outline"}
                  onClick={() => {
                    setSelectedSubcategory(subcategory)
                    setShowAllCourses(false)
                  }}
                  className="whitespace-nowrap"
                >
                  {subcategory}
                </Button>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      )}
      <div className="flex flex-wrap gap-6">
        {getDisplayedCourses().map((course, idx) => (
          <CourseCard
          slug={course?.slug}
          name={course?.name}
          image={`${BASE_URL_IMAGE}${course?.card_image}`}
          text={"Go To Course"}
          link1={`courses/${course?.slug}`}
          // text1={"Enroll"}
          // link={`dashboard/enroll`}
          description={course?.short_description}
          level={course?.course_level}
          category={course?.category || ""}
          // id={course?.id}
        />
        ))}
      </div>
    </div>
  )
}
